import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { SiteShotEntry, createSiteShot, saveSiteShot } from '../../../api/platform';
import { Modal, TableCard } from '../../../components/fs';
import { SiteDoc, usedOn } from '../../../shared/siteContent';
import { ago } from '../../admin/SsoShared';

/**
 * Site content › Screenshots (the handoff's tab 2): the library every page
 * picks from. A save is live at once on every page that uses the shot — no
 * Publish — so "Used on" and the editor's footer say where it will show.
 * "Used on" reads the PUBLISHED document: it is where a replaced image goes
 * live, which a draft's choice is not yet.
 *
 * There is no delete (the design has none): an unused shot just sits here.
 */

const monthDay = (iso: string | null) =>
  (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '');

/** "Captured from the product · Sep 26", or "Uploaded 2h ago · Ben R.". */
function whenLine(s: SiteShotEntry): string {
  const uploaded = s.desktop.source === 'upload' || s.phone?.source === 'upload';
  if (!uploaded) return `Captured from the product${s.updatedAt ? ` · ${monthDay(s.updatedAt)}` : ''}`;
  return `Uploaded ${ago(s.updatedAt) || monthDay(s.updatedAt)}${s.updatedBy ? ` · ${s.updatedBy}` : ''}`;
}

/** A "+ New …" label whose accessible name is the words alone. */
const Plus = ({ children }: { children: string }) => <><span aria-hidden="true">+</span> {children}</>;

export default function ShotsTab({ shots, published, readOnly, onSaved }: {
  shots: SiteShotEntry[];
  published: SiteDoc | null;
  readOnly: boolean;
  onSaved: (shot: SiteShotEntry, message: string) => void;
}) {
  // ONE editor at a time (rule 6): an existing shot by id, or a new one.
  const [editing, setEditing] = useState<{ id: string | null } | null>(null);
  const shot = editing?.id ? shots.find((s) => s.id === editing.id) || null : null;
  const uses = (id: string) => (published ? usedOn(published, id) : []);

  return (
    <>
      <TableCard
        className="sc-shots"
        title="Screenshots"
        action={!readOnly ? (
          <button type="button" className="fs-btn text" onClick={() => setEditing({ id: null })}>
            <Plus>Upload screenshot</Plus>
          </button>
        ) : undefined}
        footer={(
          <span>
            Desktop shots are 2880×1800 (16:10), so a page keeps the right space before the image loads. Under 640px
            wide the site shows the phone crop (780×960). With no phone crop it shrinks the desktop shot. A replaced
            file goes live on every page that uses it.
          </span>
        )}
      >
        <div className="sc-shot-grid">
          {shots.map((s) => {
            const used = uses(s.id);
            return (
              <button key={s.id} type="button" className="sc-tile" onClick={() => setEditing({ id: s.id })}>
                <span className="sc-tile-img"><img src={s.desktop.src} alt="" loading="lazy" /></span>
                <span className="sc-tile-body">
                  <span className="sc-tile-top">
                    <span className="sc-mono sc-tile-name">{s.file}</span>
                    <span className={`fs-pill ${s.phone ? 'ok' : 'neutral'}`}>{s.phone ? 'Phone crop' : 'No phone crop'}</span>
                  </span>
                  <span className="sc-tile-used">{used.length ? used.join(' · ') : 'Not on any page yet'}</span>
                  <span className="sc-tile-when">{whenLine(s)}</span>
                </span>
              </button>
            );
          })}
        </div>
      </TableCard>

      {editing && (
        <ShotEditor
          shot={shot}
          used={shot ? uses(shot.id) : []}
          taken={shots.map((s) => s.file)}
          readOnly={readOnly}
          onClose={() => setEditing(null)}
          onSaved={(saved, message) => { setEditing(null); onSaved(saved, message); }}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// The screenshot editor (760px)
// ---------------------------------------------------------------------------

interface Picked { file: File; url: string; width: number; height: number }

/** An image's own size, read in the browser for the warnings (the server reads it again). */
function readImage(file: File): Promise<Picked> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => resolve({ file, url, width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('That file is not an image this browser can read.')); };
    img.src = url;
  });
}

const SHOT_TYPES = 'image/png,image/jpeg,image/webp';
/** The name a new shot is given from its file: lower case, spaces as dashes. */
const nameFrom = (file: string) => file.trim().toLowerCase().replace(/\s+/g, '-');

function ShotEditor({ shot, used, taken, readOnly, onClose, onSaved }: {
  shot: SiteShotEntry | null;
  used: string[];
  taken: string[];
  readOnly: boolean;
  onClose: () => void;
  onSaved: (shot: SiteShotEntry, message: string) => void;
}) {
  const isNew = !shot;
  const [file, setFile] = useState('');
  const [alt, setAlt] = useState(shot?.alt || '');
  const [url, setUrl] = useState(shot?.url ?? 'form-magic.com/admin — ');
  const [desk, setDesk] = useState<Picked | null>(null);
  const [phone, setPhone] = useState<Picked | null>(null);
  const [removePhone, setRemovePhone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const deskInput = useRef<HTMLInputElement>(null);
  const phoneInput = useRef<HTMLInputElement>(null);

  // Object URLs are revoked when the dialog closes (and when replaced).
  const urls = useRef<string[]>([]);
  useEffect(() => () => { for (const u of urls.current) URL.revokeObjectURL(u); }, []);

  const pick = (set: (p: Picked) => void, isDesk: boolean) => async (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    try {
      const p = await readImage(f);
      urls.current.push(p.url);
      set(p);
      setError('');
      if (!isDesk) setRemovePhone(false);
      if (isDesk && isNew && !file) setFile(nameFrom(f.name));
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const hasPhone = !!phone || (!removePhone && !!shot?.phone);
  const phoneSrc = phone?.url || (!removePhone ? shot?.phone?.src : undefined);

  // Warnings, never blocks (the handoff's size checks).
  const deskWarn = !desk ? ''
    : Math.abs(desk.width / desk.height - 1.6) > 0.02
      ? `This image is ${desk.width}×${desk.height}, not 16:10, so pages will crop it. Captures are 2880×1800.`
      : desk.width < 1440 ? `Only ${desk.width}px wide. It will look soft on large screens.` : '';
  const phoneWarn = phone && Math.abs(phone.width / phone.height - 780 / 960) > 0.02
    ? `This image is ${phone.width}×${phone.height}, not 780×960, so the phone layout will crop it.` : '';

  const name = isNew ? nameFrom(file) : shot!.file;
  const nameTaken = isNew && !!name && taken.includes(name);
  const invalid = isNew && !desk ? 'Choose a desktop image to continue.'
    : isNew && !/^[a-z0-9][a-z0-9._-]{0,79}$/.test(name) ? 'Give it a file name, like payments.png.'
      : nameTaken ? `There is already a screenshot called ${name}.`
        : !alt.trim() ? 'Alt text is required.' : '';
  const pages = used.length;

  const save = async () => {
    setBusy(true);
    setError('');
    try {
      const body = { alt: alt.trim(), url: url.trim(), desktop: desk?.file || null, phone: phone?.file || null, removePhone };
      const r = isNew ? await createSiteShot({ ...body, file: name }) : await saveSiteShot(shot!.id, body);
      onSaved(r.shot, isNew
        ? `${r.shot.file} added to the library`
        : `${r.shot.file} saved${pages ? ` · live on ${pages} ${pages === 1 ? 'page' : 'pages'}` : ''}`);
    } catch (err) {
      setError((err as Error).message || 'The screenshot could not be saved.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={isNew ? 'Upload a screenshot' : shot!.file}
      sub={isNew
        ? 'Add it to the library, then pick it for a page on the Site tab.'
        : 'A replaced image goes live on every page that uses it as soon as you save.'}
      className="sc-shot-editor"
      onClose={onClose}
      footer={(
        <>
          <span className="sc-foot-hint">
            {invalid || (pages ? `Live on ${pages} ${pages === 1 ? 'page' : 'pages'} once saved.` : '')}
          </span>
          <button type="button" className="fs-btn primary" disabled={readOnly || busy || !!invalid} onClick={() => void save()}>
            {busy ? 'Saving…' : isNew ? 'Add screenshot' : 'Save screenshot'}
          </button>
          <button type="button" className="fs-btn neutral" onClick={onClose}>Cancel</button>
        </>
      )}
    >
      <div className="sc-form sc-shot-form">
        {error && <div className="ad-err">{error}</div>}
        <div className="sc-slots">
          <div className="sc-slot">
            <span className="sc-slot-head"><b>Desktop</b><span>2880×1800</span></span>
            <span className="sc-slot-frame desktop">
              {desk || shot ? <img src={desk?.url || shot!.desktop.src} alt="" /> : <span className="sc-slot-empty">Choose a file</span>}
            </span>
            <span className="sc-slot-actions">
              <button type="button" className="fs-btn neutral sm" disabled={readOnly} onClick={() => deskInput.current?.click()}>
                {isNew && !desk ? 'Choose image…' : 'Replace…'}
              </button>
              <span className="sc-slot-info">
                {desk ? `${desk.width}×${desk.height} · ${desk.file.name}`
                  : shot ? `${shot.desktop.width}×${shot.desktop.height} · ${shot.desktop.source === 'capture' ? 'captured' : 'uploaded'}`
                    : 'PNG, JPG or WebP'}
              </span>
            </span>
            <input ref={deskInput} type="file" accept={SHOT_TYPES} hidden aria-label="Desktop image" onChange={pick(setDesk, true)} />
            {deskWarn && <div className="sc-note warn">{deskWarn}</div>}
          </div>
          <div className="sc-slot">
            <span className="sc-slot-head"><b>Phone crop</b><span>780×960 · optional</span></span>
            <span className={`sc-slot-frame phone${hasPhone ? '' : ' none'}`}>
              {hasPhone && phoneSrc ? <img src={phoneSrc} alt="" /> : <span className="sc-slot-empty">None. Phones get the desktop shot, shrunk</span>}
            </span>
            <span className="sc-slot-actions">
              <button type="button" className="fs-btn neutral sm" disabled={readOnly} onClick={() => phoneInput.current?.click()}>
                {hasPhone ? 'Replace…' : 'Add phone crop…'}
              </button>
              {hasPhone && (
                <button
                  type="button" className="fs-btn text sm danger" disabled={readOnly}
                  onClick={() => { setPhone(null); setRemovePhone(true); }}
                >
                  Remove
                </button>
              )}
            </span>
            <input ref={phoneInput} type="file" accept={SHOT_TYPES} hidden aria-label="Phone crop image" onChange={pick(setPhone, false)} />
            {phoneWarn && <div className="sc-note warn">{phoneWarn}</div>}
          </div>
        </div>
        {isNew && (
          <div className="fs-field">
            <label htmlFor="sc-shot-file">File name</label>
            <input id="sc-shot-file" type="text" className="sc-mono" value={file} placeholder="e.g. payments.png" onChange={(e) => setFile(e.target.value)} />
          </div>
        )}
        <div className="fs-field">
          <label htmlFor="sc-shot-alt">Alt text</label>
          <textarea
            id="sc-shot-alt" rows={2} value={alt} disabled={readOnly}
            placeholder="What the screenshot shows, for screen readers" onChange={(e) => setAlt(e.target.value)}
          />
        </div>
        <div className="fs-field">
          <label htmlFor="sc-shot-url">Browser bar text</label>
          <input id="sc-shot-url" type="text" value={url} disabled={readOnly} onChange={(e) => setUrl(e.target.value)} />
          <span className="hint">Shown in the frame above the shot on the Features page.</span>
        </div>
        <div className="sc-used">
          <span className="sc-field-label">Used on</span>
          <span className="sc-chips">
            {used.length
              ? used.map((u) => <span key={u} className="sc-chip">{u}</span>)
              : <span className="sc-faint">No page yet. Pick it for a page on the Site tab.</span>}
          </span>
        </div>
      </div>
    </Modal>
  );
}
